/**
 * CoachWhisper — proactive Dashboard pull-back into the Coach. When
 * the welcome endpoint detects an unfinished thread (kind === 'continuity'
 * or 'pivot'), we render a small accent banner inside Zone 2.
 * Clicking it preseeds the Coach with the right continuity prompt
 * and navigates over — so the user lands ON the coach already in mid-
 * conversation, not at a cold start.
 *
 * Hidden when:
 *   - Welcome endpoint says first_time (no thread to pick up)
 *   - User has already dismissed it this session
 *   - Welcome fetch fails (silent — we never block the dashboard)
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, MessageCircleHeart, X } from 'lucide-react';
import { pieApi, pieCoachPreseed } from '../pieApi';
import logger from '../../../lib/logger';

export default function CoachWhisper() {
  const [welcome, setWelcome] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const navigate = useNavigate();
  const firedRef = React.useRef(false);

  useEffect(() => {
    if (firedRef.current) return undefined;
    firedRef.current = true;
    pieApi
      .coachWelcome()
      .then(setWelcome)
      .catch((e) => logger.warn('coachWelcome failed on dashboard', e));
    return undefined;
  }, []);

  if (!welcome || dismissed) return null;
  if (welcome.kind === 'first_time') return null;

  const continuityPrompt = welcome.kind === 'pivot'
    ? "Yes — help me figure out what to try instead."
    : "Yes — keep us on that thread. What should I do this week to keep the momentum?";

  const onPickUp = () => {
    pieCoachPreseed.set(continuityPrompt);
    navigate('/mirror/coach');
  };

  return (
    <div className="pie-coach-whisper" data-testid="pie-coach-whisper">
      <span className="pie-coach-whisper-icon" aria-hidden>
        <MessageCircleHeart size={14} />
      </span>
      <span className="pie-coach-whisper-eyebrow pie-mono">
        Coach has something to say
      </span>
      <span className="pie-coach-whisper-text">{welcome.greeting}</span>
      <button
        type="button"
        className="pie-btn pie-btn-primary pie-coach-whisper-cta"
        onClick={onPickUp}
        data-testid="pie-coach-whisper-cta"
      >
        Pick it up <ArrowRight size={12} aria-hidden />
      </button>
      <button
        type="button"
        className="pie-whats-changed-dismiss"
        onClick={() => setDismissed(true)}
        data-testid="pie-coach-whisper-dismiss"
        aria-label="Dismiss"
      >
        <X size={12} aria-hidden />
      </button>
    </div>
  );
}
